import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatDateWithYear } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  ApiError,
  type ApiTokenRow,
  type CreateApiTokenResponse,
  type TenantUser,
} from '../types/api';

type TokenRole = 'editor' | 'viewer';

function tokenStatus(t: ApiTokenRow): 'Revoked' | 'Expired' | 'Active' {
  if (t.revokedAt !== null) return 'Revoked';
  return t.expiresAt <= Date.now() ? 'Expired' : 'Active';
}

function createErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  if (err instanceof ApiError && err.status === 404)
    return 'That user is not a member of this account.';
  if (err instanceof ApiError && err.body.message) return err.body.message;
  return 'The token could not be created. Try again.';
}

/**
 * Create form. The plaintext token only lives in this component's mutation
 * state: shown once, never put in storage or the query cache.
 */
function CreateTokenCard({
  users,
  productionScope,
}: {
  users: TenantUser[];
  productionScope: boolean;
}) {
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<TokenRole>('editor');
  const [days, setDays] = useState('90');
  const [totpCode, setTotpCode] = useState('');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();
  const nameId = useId();
  const userIdId = useId();
  const roleId = useId();
  const daysId = useId();
  const codeId = useId();
  const tokenId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<CreateApiTokenResponse>('/api-tokens', {
        method: 'POST',
        body: {
          name: name.trim(),
          userId,
          role,
          expiresInDays: Number(days),
          totpCode: totpCode.replace(/\s/g, ''),
        },
      }),
    onSuccess: () => {
      setName('');
      setTotpCode('');
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });

  const created = mutation.data;
  const canSubmit =
    name.trim().length > 0 &&
    userId !== '' &&
    totpCode.replace(/\s/g, '').length >= 6 &&
    !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  async function copyToken() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Card className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold m-0">Create API token</h2>
      <p className="m-0 text-sm text-muted">
        The token acts as the chosen user, never above Editor. It can never approve or send a
        campaign, export contacts or manage users and settings. Creating one needs a fresh code from
        your authenticator app.
      </p>
      {productionScope ? (
        <p className="m-0 text-sm text-muted">
          Here a token can hand over order history and write newsletters: templates, campaign
          drafts, images and test sends to team members. It can read contacts one page at a time,
          statistics and counts, and it cannot change lists, segments, automations or the brand kit.
        </p>
      ) : null}
      <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5 min-w-[200px] flex-1">
          <Label htmlFor={nameId}>Name</Label>
          <Input
            id={nameId}
            autoComplete="off"
            maxLength={80}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={userIdId}>Acts as</Label>
          <select
            id={userIdId}
            className="h-[42px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          >
            <option value="">Choose a user</option>
            {users
              .filter((u) => u.disabledAt === null)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={roleId}>Role</Label>
          <select
            id={roleId}
            className="h-[42px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as TokenRole)}
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={daysId}>Expires after</Label>
          <select
            id={daysId}
            className="h-[42px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
            value={days}
            onChange={(e) => setDays(e.target.value)}
          >
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="365">365 days</option>
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={codeId}>Your code from the authenticator app</Label>
          <Input
            id={codeId}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123 456"
            className="font-mono text-base tracking-widest"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            required
          />
        </div>
        <Button type="submit" disabled={!canSubmit}>
          {mutation.isPending ? 'Creating…' : 'Create token'}
        </Button>
      </form>

      {mutation.isError ? <Alert>{createErrorText(mutation.error)}</Alert> : null}

      {created ? (
        <div className="flex flex-col gap-1.5" role="status">
          <Label htmlFor={tokenId}>New token: {created.apiToken.name}</Label>
          <div className="flex gap-2">
            <Input
              id={tokenId}
              readOnly
              value={created.token}
              className="font-mono"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button type="button" variant="secondary" onClick={() => void copyToken()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="m-0 text-sm text-muted">
            Copy it now – you will not see this again. It expires{' '}
            {formatDateWithYear(created.apiToken.expiresAt)}.
          </p>
        </div>
      ) : null}
    </Card>
  );
}

function TokenRowView({ token }: { token: ApiTokenRow }) {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();
  const status = tokenStatus(token);

  const revoke = useMutation({
    mutationFn: () => apiFetch(`/api-tokens/${token.id}/revoke`, { method: 'POST', body: {} }),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });

  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 pr-3">{token.name}</td>
      <td className="py-2 pr-3">{token.userEmail}</td>
      <td className="py-2 pr-3">{token.role === 'editor' ? 'Editor' : 'Viewer'}</td>
      <td className="py-2 pr-3">{formatDateWithYear(token.createdAt)}</td>
      <td className="py-2 pr-3">{formatDateWithYear(token.expiresAt)}</td>
      <td className="py-2 pr-3">
        {token.lastUsedAt === null ? 'Never' : formatDateTime(token.lastUsedAt)}
      </td>
      <td className="py-2 pr-3">{status}</td>
      <td className="py-2 pl-3 text-right">
        {status !== 'Active' ? null : confirming ? (
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-xs text-muted">
              Revoke "{token.name}"? It stops working immediately.
            </span>
            {revoke.isError ? (
              <span className="text-xs text-danger-text">Could not revoke. Try again.</span>
            ) : null}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={revoke.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                aria-label={`Confirm: revoke ${token.name}`}
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
              >
                {revoke.isPending ? 'Revoking…' : 'Confirm: revoke'}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Revoke ${token.name}`}
            onClick={() => setConfirming(true)}
          >
            Revoke
          </Button>
        )}
      </td>
    </tr>
  );
}

/**
 * Settings → API tokens (docs/decisions.md "API tokens for Claude, staging
 * first"; production narrowed to `PRODUCTION_API_TOKEN_ROUTES` unless
 * `API_TOKENS_FULL_EDITOR` is set, 23 Oct 2026). Admin-only.
 * The whole section is hidden when the API answers 404 (the
 * `API_TOKENS_ENABLED` flag is off).
 */
export function ApiTokensPage() {
  const { isAdmin } = useAuth();

  const tokens = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => apiFetch<{ tokens: ApiTokenRow[]; productionScope?: boolean }>('/api-tokens'),
    enabled: isAdmin,
    retry: false,
  });
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<{ users: TenantUser[] }>('/users'),
    enabled: isAdmin,
  });

  const notAvailable = tokens.error instanceof ApiError && tokens.error.status === 404;

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">API tokens</h1>
        <MockBadge />
      </header>

      {!isAdmin ? (
        <Card className="p-4 flex flex-col gap-2">
          <Alert>Only administrators can manage API tokens in this account.</Alert>
        </Card>
      ) : notAvailable ? (
        <Card className="p-4 flex flex-col gap-2">
          <p className="m-0 text-sm text-muted">API tokens are not available here.</p>
        </Card>
      ) : (
        <>
          <CreateTokenCard
            users={users.data?.users ?? []}
            productionScope={tokens.data?.productionScope === true}
          />

          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-semibold m-0">Tokens</h2>
            {tokens.isLoading ? <div className="text-muted text-sm">Loading tokens…</div> : null}
            {tokens.isError ? <Alert>The tokens could not be loaded.</Alert> : null}
            {tokens.data ? (
              tokens.data.tokens.length === 0 ? (
                <div className="text-muted text-sm">No API tokens yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-muted border-b border-border">
                        <th className="py-2 pr-3 font-medium">Name</th>
                        <th className="py-2 pr-3 font-medium">Acts as</th>
                        <th className="py-2 pr-3 font-medium">Role</th>
                        <th className="py-2 pr-3 font-medium">Created</th>
                        <th className="py-2 pr-3 font-medium">Expires</th>
                        <th className="py-2 pr-3 font-medium">Last used</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pl-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tokens.data.tokens.map((t) => (
                        <TokenRowView key={t.id} token={t} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </Card>
        </>
      )}
    </>
  );
}
